/****************************************************
 * Module Name:  wolzaMarketSell.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import React, { useEffect, useState } from "react";
import { DataGridPro, GridToolbarExport, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarFilterButton, GridToolbarQuickFilter } from "@mui/x-data-grid-pro";
import { Box, Button, TextField, Tooltip, Typography, } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import CircularProgress from "@mui/material/CircularProgress";
import Utility from "../../utility/utility";
import Constants from "../../utility/constants";
import renderCellExpand from "../common/dataGridCellExpand";
import { Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg'
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import WolzaIndiaExportProxy from "../../utility/proxy/nivodautility/wolzaIndiaExportProxy";
import MarketSellWolzaCriteriaWise from "../common/marketSellWolzaCriteriaWise";
import WorkersettingsProxy from "../../utility/proxy/worker/workersettingsProxy";
import CommonSnackbar from "../common/commonSnackBar";
import ClearIcon from '@mui/icons-material/Clear';
import PageTitle from "../common/pageTitle";

const WolzaMarketSell = () => {
    const [listData, setListData] = useState([]);
    const [listLoader, setListLoader] = useState(false);

    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [wolzaSearchCriteria, setWolzaSearchCriteria] = useState({});
    const [showWolzaDetailModal, setShowWolzaDetailModal] = useState(false);
    const [snackBarTitle, setSnackBarTitle] = useState("");
    const [snackBarMessage, setSnackBarMessage] = useState("");
    const [snackBarVariant, setSnackBarVariant] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [filterValues, setFilterValues] = useState({});

    // eslint-disable-next-line
    const [local_Permission, setLocal_Permission] = useState(
        Constants.USER_PERMISSION.filter(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.ANALYSIS_WOLZA_MARKETSELL
        )
    );

    useEffect(() => {
        PageTitle("ANALYSIS_WOLZA_MARKETSELL");
        getDefaultDate();
    }, [])

    const getDefaultDate = () => {

        WorkersettingsProxy.GetSingleWorkersetting(Constants.DefaultWolzaLastFetchedDateSetting,
            (responseData) => {
                if (responseData && responseData.settingvalue !== "") {
                    const { firstDate, lastDate } = Utility.getFirstAndLastDate(responseData.settingvalue);

                    const dateFormat = 'DD-MM-YYYY';
                    const now = dayjs();

                    // Parse dates with the specified format
                    const parsedFirstDate = dayjs(firstDate, dateFormat);
                    const parsedLastDate = dayjs(lastDate, dateFormat);

                    if (parsedFirstDate.isValid() && parsedLastDate.isValid()) {
                        // Set time to current system time
                        const fromDate = parsedFirstDate
                            .hour(now.hour())
                            .minute(now.minute())
                            .second(now.second())
                            .millisecond(now.millisecond())
                            .toDate();
                        setFromDate(fromDate);

                        const toDate = parsedLastDate
                            .hour(now.hour())
                            .minute(now.minute())
                            .second(now.second())
                            .millisecond(now.millisecond())
                            .toDate();
                        setToDate(toDate);
                    }
                }
            });
    }
    const handleWolzaDetail = (params) => {
        setWolzaSearchCriteria(params);
        setShowWolzaDetailModal(true);
    }

    const handleSearch = () => {
        let data = {
            from: (fromDate !== null && fromDate !== "") ?
                dayjs(fromDate).format('YYYY-MM-DD') + "T00:00:00.000Z" : null,
            to: (toDate !== null && toDate !== "") ?
                dayjs(toDate).format('YYYY-MM-DD') + "T23:59:59.999Z" : null,
        }

        setListLoader(true);
        WolzaIndiaExportProxy.GetMarketSell(
            data,
            (responseData) => {
                if (responseData) {
                    const uniqueFilterValues = {
                        shape: Utility.getDistinctOptions(responseData, "shape"),
                        cut: Utility.getDistinctOptions(responseData, "cut"),
                        clarity: Utility.getDistinctOptions(responseData, "clarity"),
                        pol: Utility.getDistinctOptions(responseData, "pol"),
                        sym: Utility.getDistinctOptions(responseData, "sym"),
                        flo: Utility.getDistinctOptions(responseData, "flo"),
                        color: Utility.getDistinctOptions(responseData, "color"),
                        stonetype: Utility.getDistinctOptions(responseData, "stonetype"),
                        lab: Utility.getDistinctOptions(responseData, "lab"),
                    };
                    setFilterValues(uniqueFilterValues);
                    setListData(responseData);
                }
                setListLoader(false);
            });
    };

    const columnsData = [
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 190,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.shape : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "sizerange",
            headerName: "SIZE RANGE",
            headerAlign: "center",
            align: "center",
            width: 130,
            type: "singleSelect",
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.color : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.clarity : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.cut : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.pol : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.sym : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.flo : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.lab : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 180,
            type: "singleSelect",
            valueOptions: filterValues ? filterValues.stonetype : [],
            filterOperators: Utility.combinedFilters(),
            renderCell: (params) => params.value ? params.value.toUpperCase() : '',
        },
        {
            field: "stonecount",
            headerName: "STONE COUNT",
            headerAlign: "center",
            align: "center",
            minWidth: 60,
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) =>
                <Button
                    sx={{ textDecorationLine: "underline" }}
                    onClick={() => handleWolzaDetail(params)}
                >
                    {params.value}
                </Button>
        },
        {
            field: "total_weight",
            headerName: "TOTAL WEIGHT",
            headerAlign: "center",
            align: "center",
            minWidth: 60,
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "total_price",
            headerName: "TOTAL PRICE",
            headerAlign: "center",
            align: "center",
            minWidth: 60,
            flex: 1,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
    ];

    const rows = listData;
    const columns = columnsData;
    const CustomToolbar = () => {
        return (
            <GridToolbarContainer
                sx={{
                    display: "flex",
                    alignItems: "center",
                    marginHorizontal: "5px",
                    "&.MuiDataGrid-toolbarContainer": {
                        padding: "0px 4px !important",
                    },
                }}
            >
                <GridToolbarColumnsButton />
                <GridToolbarFilterButton />
                <GridToolbarExport />
                <Box sx={{ flexGrow: 1, display: { sm: "none", md: "block" } }} />
                {listLoader ? (
                    <CircularProgress size="1rem" />
                ) : (
                    <Tooltip title="Reload List">
                        <RefreshOutlinedIcon
                            onClick={() => {
                                if (fromDate && fromDate !== "" && toDate && toDate !== null) {
                                    handleSearch();
                                }
                                else {
                                    setShowSnackbar(true);
                                    setSnackBarMessage("please select date range");
                                    setSnackBarVariant("warning");
                                    setSnackBarTitle("Warning");
                                }
                            }}
                            color="primary"
                            fontSize="medium"
                            sx={{ cursor: "pointer", mr: 1 }}
                        />
                    </Tooltip>
                )}

                <GridToolbarQuickFilter debounceMs={500} />
            </GridToolbarContainer>
        );
    }
    return (

        <div>
            {showSnackbar ? (
                <CommonSnackbar
                    show={showSnackbar}
                    message={snackBarMessage}
                    variant={snackBarVariant}
                    autoHide={false}
                    title={snackBarTitle}
                    handleClose={() => { setShowSnackbar(false); }}
                />)
                : null}
            {showWolzaDetailModal ? (
                <Modal
                    show={showWolzaDetailModal}
                    onHide={() => setShowWolzaDetailModal(false)}
                    dialogClassName="max-modalSize"
                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;
                            Wolza Details
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <MarketSellWolzaCriteriaWise
                            searchCriteria={wolzaSearchCriteria}
                            fromDate={fromDate}
                            toDate={toDate}
                        />
                    </Modal.Body>
                </Modal>
            ) : null}
            <div>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "100%",
                        gap: 2,
                        my: 2,
                        ml: { xs: 0, sm: 2 },
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "15px",
                            width: "100%",
                            alignItems: "center",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: "10px",
                                width: { xs: "100%", sm: "auto" },
                                "& .MuiOutlinedInput-root": {
                                    height: "2.45rem",
                                },
                            }}
                        >
                            <Typography mt={{ xs: 0, sm: 1 }}>From</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    renderInput={(props) => (
                                        <TextField
                                            fullWidth
                                            {...props}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    value={fromDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        const now = dayjs();
                                        const fromDate = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();
                                        setFromDate(fromDate);
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: "column", sm: "row" },
                                alignItems: { xs: "flex-start", sm: "center" },
                                gap: "10px",
                                width: { xs: "100%", sm: "auto" },
                                "& .MuiOutlinedInput-root": {
                                    height: "2.45rem",
                                },
                            }}
                        >
                            <Typography mt={{ xs: 0, sm: 1 }}>To</Typography>
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DatePicker
                                    className="picker"
                                    renderInput={(props) => (
                                        <TextField
                                            fullWidth
                                            {...props}
                                            inputProps={{ ...props.inputProps, readOnly: true }}
                                        />
                                    )}
                                    value={toDate}
                                    inputFormat="DD/MM/YYYY"
                                    onChange={(newValue) => {
                                        const now = dayjs();
                                        const toDate = dayjs(newValue)
                                            .hour(now.hour())
                                            .minute(now.minute())
                                            .second(now.second())
                                            .millisecond(now.millisecond())
                                            .toDate();
                                        setToDate(toDate);
                                    }}
                                />
                            </LocalizationProvider>
                        </Box>

                        <Box mt={{ xs: 0, sm: "0.4rem" }}>
                            <Tooltip title="Clear Dates">
                                <ClearIcon
                                    onClick={() => {
                                        setFromDate(null);
                                        setToDate(null);
                                    }}
                                    color="primary"
                                    fontSize="medium"
                                    sx={{ cursor: "pointer", mr: 1 }}
                                />
                            </Tooltip>
                        </Box>

                        <Box
                            sx={{
                                "& .Mui-disabled": {
                                    backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                    color: "rgba(0, 0, 0, 0.26) !important",
                                },
                                width: { xs: "100%", sm: "auto" },
                                alignSelf: { xs: "stretch", sm: "center" },
                            }}
                        >
                            <Button
                                variant="contained"
                                fullWidth={true}
                                size="medium"
                                onClick={() => {
                                    if (fromDate && toDate) {
                                        handleSearch();
                                    } else {
                                        setShowSnackbar(true);
                                        setSnackBarMessage("please select date range");
                                        setSnackBarVariant("warning");
                                        setSnackBarTitle("Warning");
                                    }
                                }}
                                startIcon={<SearchOutlinedIcon size="large" />}
                                sx={{ ml: { xs: 0, sm: 2 } }}
                            >
                                Search
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box
                    sx={{
                        px: 1,
                        width: "100%",
                        position: "relative",
                        backgroundColor: '#fff',
                        color: '#000',
                        "& .MuiTablePagination-selectLabel": {
                            marginTop: "15px",
                        },
                        "& .MuiTablePagination-displayedRows": {
                            marginTop: "15px",
                        },
                        "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        "& .css-1knaqv7-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "4px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-znmrd6-MuiButtonBase-root-MuiButton-root": {
                            color: "#1976d2",
                            padding: "5px 5px",
                            borderRadius: "4px",
                        },
                        "& .css-cc8tf1": {
                            fontWeight: Constants.defaultFontWeight,
                        },
                        height: "75vh",
                    }}
                >
                    <DataGridPro
                        sx={{
                            '& .MuiDataGrid-columnHeader': {
                                color: '#fff',
                                backgroundColor: 'grey',
                            },
                            "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                py: "6px",
                            },
                            "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                py: "12px",
                            },
                            "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                py: "18px",
                            },
                            fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                        }}
                        density="compact"
                        disableRowSelectionOnClick={true}
                        slots={{ toolbar: CustomToolbar }}
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.id}
                        loading={listLoader}
                        initialState={{
                            columns: {
                                columnVisibilityModel: {
                                    // Hide columns id, the other columns will remain visible
                                },
                            },
                            pagination: {
                                paginationModel: Constants.pageSize,
                            },
                        }}
                        pageSizeOptions={Constants.pageSizeOptions}
                        pagination
                        slotProps={{
                            toolbar: {
                                showQuickFilter: true,
                                quickFilterProps: {
                                    debounceMs: 500,
                                },
                            },
                        }}
                    />
                </Box>
            </div>
        </div>
    );
};
export default WolzaMarketSell;
